import React, { useEffect } from 'react'
import { MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Button, Flex, HighlightedText } from '@ui'
import { IUIButtonProps } from '@ui/buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import { get } from 'lodash'
import api, { IBuildingType } from '@api'
import MultiSelect from '@ui/selects/MultiSelect'

interface BuildingTypeSelectProps {
  values: IBuildingType[]
  onChange: (buildingType: string | IBuildingType) => void
  buttonProps?: IUIButtonProps
}

export default function BuildingTypeSelect({
  values,
  onChange,
  buttonProps,
}: BuildingTypeSelectProps) {
  return (
    <MultiSelect
      title="Building Types"
      values={values.map((v) => (
        <span key={v.id}>{v.displayName}</span>
      ))}
      select={<BTSelect {...buttonProps} multi={true} values={values} onChange={onChange} />}
      onRemove={(removedBt) => {
        onChange(removedBt)
      }}
    />
  )
}

const filterSpaceTypes: ItemListPredicate<IBuildingType> = (query, buildingTypes) => {
  return query ? fuzzy.filter(buildingTypes, query, { key: 'displayName' }) : buildingTypes
}

let persistedState: IBuildingType[] = []

export function useBuildingTypes(): IBuildingType[] {
  const [spaceTypes, setBuildingTypes] = React.useState<IBuildingType[]>(persistedState)

  useEffect(() => {
    api.company.buildingTypes().then((buildingTypes) => setBuildingTypes(buildingTypes))
  }, [])

  useEffect(() => {
    // Cache initial list
    if (!persistedState.length && spaceTypes.length) {
      persistedState = spaceTypes
    }
  })

  return spaceTypes
}

export function BTSelect({
  multi,
  onChange,
  values,
  buttonProps = { minimal: true, iconName: 'plus', blue: true, text: 'Add' },
}: {
  multi?: boolean | undefined
  onChange?: (buildingType: IBuildingType) => void
  values?: IBuildingType[]
  buttonProps?: IUIButtonProps
}) {
  const buildingTypes: IBuildingType[] = useBuildingTypes()

  const BTSelect = Select2<IBuildingType>

  return (
    <BTSelect
      resetOnSelect
      popoverProps={{
        minimal: true,
        position: Position.BOTTOM,
      }}
      noResults={<MenuItem disabled text="No results" />}
      items={buildingTypes}
      itemRenderer={(buildingType, { handleClick, modifiers, query }) =>
        modifiers.matchesPredicate ? (
          <MenuItem
            active={modifiers.active}
            key={buildingType.name}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              if (multi) {
                e.preventDefault()
                onChange?.(buildingType)
                return
              }
              handleClick(e)
            }}
            text={
              <Flex align="center">
                {multi && <Checkbox checked={!!values?.find((name) => buildingType === name)} />}
                <HighlightedText text={buildingType.displayName} query={query} />
              </Flex>
            }
          />
        ) : null
      }
      itemListPredicate={filterSpaceTypes}
      onItemSelect={(item) => onChange?.(item)}
    >
      <Button
        {...buttonProps}
        text={!multi && values?.length ? values[0].displayName : buttonProps.text}
        rightIconName={!get(buttonProps, 'iconName') && !values ? 'chevron-down' : undefined}
      />
    </BTSelect>
  )
}
